"use client";

import { useEffect } from "react";
import { useRouter, usePathname } from "next/navigation";
import { useWorkspace } from "@/lib/context/WorkspaceContext";
import { isEventHelperFor } from "@/lib/permissions";
// Routes where we should NOT redirect even if profile is incomplete
// `/verify-discord` is the Discord server's front door: it has to render for
// someone with no session and no completed profile, or the gate bounces every
// arrival to /onboarding before they ever see why they were sent there.
const AUTH_ROUTES = ["/login", "/signup", "/forgot-password", "/onboarding", "/verify-discord"];

export default function OnboardingGate({ children }: { children: React.ReactNode }) {
    const router = useRouter();
    const pathname = usePathname();
    const { user, isLoading } = useWorkspace();

    const isAuthRoute = AUTH_ROUTES.some(r => pathname.startsWith(r));

    /**
     * A temporary event helper is not made to complete onboarding to reach the
     * one event they were asked to help with.
     *
     * Onboarding asks for a profile photo and a LinkedIn URL, which is the wrong
     * thing to demand of a venue coordinator helping for one afternoon — and SSO
     * creates accounts with `is_profile_complete = false`, so without this they
     * would be bounced to /onboarding and never reach the event at all.
     *
     * Scoped to that event's own pages on purpose: the moment they navigate
     * anywhere else in the platform the normal gate applies again, and once the
     * grant lapses they are an ordinary incomplete-profile user once more.
     */
    const helperMeetingId = pathname.match(/\/meetings\/([0-9a-fA-F-]{36})/)?.[1] || null;
    const isHelperOnEventPage = isEventHelperFor(user, helperMeetingId);

    useEffect(() => {
        if (isLoading) return;
        if (!user) return;
        if (isAuthRoute) return;
        if (isHelperOnEventPage) return;

        const needsCoc = (user.coc_accepted_version ?? null) !== (user.coc_current_version ?? null);
        if (!user.is_profile_complete || needsCoc) {
            router.replace("/onboarding");
        }
    }, [user, isLoading, pathname, router, isAuthRoute, isHelperOnEventPage]);

    return (
        <>
            {children}
        </>
    );
}
